/* eslint-disable @typescript-eslint/restrict-template-expressions */
import {
  Button,
  useTheme,
  Grid,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { type ChangeEvent, useState } from 'react';
import { colorTheme } from '../../../theme';
import { createAnduploadBankFile } from 'service';
import {
  setSnackBarFailed,
  setSnackBarInfo,
  setSnackBarSuccess,
} from 'store/snackBar';
import { strings } from 'i18n/Strings';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'store';
import Buttons from 'components/elements/Buttons';
import { CustomDataGrid } from 'components';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { commonConstantTexts } from 'constants/constantText';

interface IUploadFile {
  getBankFile: () => void;
  onClose: () => void;
  onError: () => void;
}
export interface UploadFilesType {
  file: File;
  orgId: string;
}
const UploadFile = (props: IUploadFile): JSX.Element => {
  type GridRowData = Record<string, unknown>;
  const currentTheme = useTheme();
  const colors = colorTheme(currentTheme.palette.mode);
  const [errData, setErrData] = useState([]);
  const [formData, setFormData] = useState({ file: null, orgId: null });
  const [filename, setFileName] = useState('');
  const [loader, setLoader] = useState(false);
  const { empId } = useSelector((state: RootState) => state.onboarding);
  const dispatch = useDispatch();
  const handleOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const name = e.target.name;
    const value = e.target.type === 'file' ? e.target.files[0] : e.target.value;
    if (e.target.type === 'file') {
      setFileName(e.target.files[0].name);
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  const uploadBankDataFile = async (): Promise<void> => {
    setLoader(true);
    try {
      const data = await createAnduploadBankFile(formData, empId);
      if (data.mismatchDebtorNameOrg && data.mismatchDebtorNameOrg.length > 0) {
        const orgIds = data.mismatchDebtorNameOrg.join(',');
        dispatch(
          setSnackBarInfo({
            snackBarMessage: `${data.message}! ${strings.displayText.mismtachDebtorName}: ${orgIds}`,
          }),
        );
      } else {
        dispatch(
          setSnackBarSuccess({
            snackBarMessage: data.message,
          }),
        );
      }

      props.onClose();
      props.getBankFile();
    } catch (error: unknown) {
      setLoader(false);

      if (error?.response?.data?.data?.length) {
        setErrData(error.response.data.data);
        props.onError();
      } else {
        dispatch(
          setSnackBarFailed({
            snackBarMessage:
              error?.response?.data?.message || 'An error occurred.',
          }),
        );
        props.onClose();
      }
    }
    setLoader(false);
  };

  const styles = {
    uploadBankFile: {
      height: 50,
      width: '100%',
      bgcolor: colors.greenAccent[500],
      textTransform: 'none',
      padding: '0 3rem 0 3rem',
    },
    filename: {
      textAlign: 'center',
    },
    organizationTextField: {
      '& p': {
        color: 'red',
      },
      mb: 2,
    },
    submitGrid: {
      display: 'grid',
    },
    submitBtn: {
      height: 50,
      bgcolor: colors.greenAccent[500],
      textTransform: 'none',
    },
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'Date',
      width: 100,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Date
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.Date || '-'}
        </Typography>
      ),
    },
    {
      field: 'Account / IBAN',
      width: 190,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Account / IBAN
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row['Account / IBAN'] || '-'}
        </Typography>
      ),
    },
    {
      field: 'Currency',
      width: 100,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Currency
        </Typography>
      ),

      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.Currency || '-'}
        </Typography>
      ),
    },
    {
      field: 'Amount',
      width: 100,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Amount
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.Amount || '-'}
        </Typography>
      ),
    },
    {
      field: 'Balance',
      width: 100,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Balance
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.Balance || '-'}
        </Typography>
      ),
    },
    {
      field: 'Credit / Debit',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Credit / Debit
        </Typography>
      ),

      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row['Credit / Debit'] || '-'}
        </Typography>
      ),
    },
    {
      field: 'Creditor Name',
      width: 170,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Creditor Name
        </Typography>
      ),

      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row['Creditor Name'] || '-'}
        </Typography>
      ),
    },

    {
      field: 'Debtor Name',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Debtor Name
        </Typography>
      ),

      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row['Debtor Name'] || '-'}
        </Typography>
      ),
    },

    {
      field: 'Transaction Narrative',
      width: 170,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Transaction Narrative
        </Typography>
      ),

      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row['Transaction Narrative'] || '-'}
        </Typography>
      ),
    },
    {
      field: 'Transaction Reference',
      width: 170,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Transaction Reference
        </Typography>
      ),

      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row['Transaction Reference'] || '-'}
        </Typography>
      ),
    },
    {
      field: 'error',
      width: 270,
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Error
        </Typography>
      ),

      renderCell: ({ row }) => (
        <Typography
          variant="h4"
          color={colors.primaryRed[100]}
          fontSize={12}
          sx={{ whiteSpace: 'pre-wrap' }}
        >
          {row?.error || '-'}
        </Typography>
      ),
    },
  ];

  return (
    <>
      {errData.length ? (
        <Box>
          <CustomDataGrid
            currentPage={strings.displayText.banFilesTitle}
            columns={columns}
            rows={errData}
            getRowId={(row: GridRowData) =>
              // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
              `${String(row['Account / IBAN'])}_${row.error}_${row.Amount}`
            }
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
                ? commonConstantTexts.evenText
                : commonConstantTexts.oddText
            }
            pageCount={errData.length}
          />
        </Box>
      ) : (
        <>
          <Grid item xs={6} my={2}>
            <Button
              variant="contained"
              sx={styles.uploadBankFile}
              component="label"
            >
              {strings.displayText.uploadBankFile}
              <input
                onChange={handleOnChange}
                accept=".csv,.xlsx,text/xml"
                type="file"
                hidden
                name="file"
              />
            </Button>
          </Grid>
          <Grid sx={styles.filename}>{filename}</Grid>
          <Grid sx={styles.submitGrid}>
            <Buttons
              variant="contained"
              sx={styles.submitBtn}
              component="label"
              onClick={() => {
                void uploadBankDataFile();
              }}
              disabled={loader}
              endIcon={loader ? <CircularProgress size={15} /> : null}
            >
              {strings.displayText.submitBtn}
            </Buttons>
          </Grid>
        </>
      )}
    </>
  );
};
export default UploadFile;
